import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Grid,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

export default function StaffProfile() {
  const [searchCode, setSearchCode] = useState("");
  const [form, setForm] = useState({
    staff_code: "",
    salutation: "",
    staff_name: "",
    aadhar_no: "",
    dob: "",
    doj: "",
    dor: "",
    gender: "",
    qualification: "",
    designation: "",
    staff_type: "",
    doj_time: "",
    dor_time: "",
    mobile: "",
    email: "",
    staff_status: "ACTIVE",
  });
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const user = JSON.parse(sessionStorage.getItem("user"));
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const collegeId = user?.college_id;
  const clientId = user?.client_id;

  // Fetch staff by code
  const fetchStaff = async (staffcode) => {
    if (!staffcode) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/staff/staffcode/${clientId}/${collegeId}/${staffcode}`
      );
      if (data.success && data.staff) {
        const s = data.staff;
        setForm({
          staff_code: s.staff_code,
          salutation: s.salutation || "",
          staff_name: s.staff_name || "",
          aadhar_no: s.aadhar_no || "",
          dob: s.dob ? new Date(s.dob).toISOString().substring(0, 10) : "",
          doj: s.doj ? new Date(s.doj).toISOString().substring(0, 10) : "",
          dor: s.dor ? new Date(s.dor).toISOString().substring(0, 10) : "",
          gender: s.gender || "",
          qualification: s.qualification || "",
          designation: s.designation || "",
          staff_type: s.staff_type || "",
          doj_time: s.doj_time || "",
          dor_time: s.dor_time || "",
          mobile: s.mobile ? s.mobile.replace(/\n/g, " ") : "",
          email: s.email || "",
          staff_status: s.staff_status || "ACTIVE",
        });
        setEditing(s);
        setOpen(true);
      } else {
        showSnackbar("Staff not found", "error");
      }
    } catch (err) {
      console.error(err);
      showSnackbar(err?.response?.data?.message || "Failed to fetch staff", "error");
    } finally {
      setLoading(false);
    }
  };

  // Submit update
  const handleSubmit = async () => {
    if (!editing) return;
    const payload = {
      ...form,
      dob: form.dob ? new Date(form.dob) : null,
      doj: form.doj ? new Date(form.doj) : null,
      dor: form.dor ? new Date(form.dor) : null,
      updated_on: new Date(),
      college_id: Number(collegeId),
      client_id: Number(clientId),
    };
    try {
      await axios.put(
        `${API_BASE_URL}/api/staff/update/${clientId}/${collegeId}/${editing.staff_id}`,
        payload
      );
      showSnackbar("Staff updated successfully", "success");
      setOpen(false);
    } catch (err) {
      console.error(err);
      showSnackbar(err?.response?.data?.message || "Update failed", "error");
    }
  };

  return (
    <Box p={2}>
      <Paper elevation={1} sx={{ p: 2 }}>
        <Typography variant="h6" mb={2}>
          UPDATE STAFF PROFILE
        </Typography>
        <Stack direction="row" spacing={2}>
          <TextField
            size="medium"
            placeholder="Enter Staff Code"
            value={searchCode}
            onChange={(e) => setSearchCode(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") fetchStaff(searchCode);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => fetchStaff(searchCode)}>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            fullWidth
            sx={{
              width: {
                xs: "100%",
                sm: 250,
                md: 300,
              },
              "& .MuiInputBase-root": {
                height: 40, // reduce height
                fontSize: "1.2rem", // increase text size
                fontWeight: 600,
              },
              "& .MuiInputBase-input": {
                padding: "8px 12px",
                fontSize: "1rem",
              },
              "& .MuiInputBase-input::placeholder": {
                fontSize: "1rem",
                opacity: 1,
              },
            }}
          />
        </Stack>
      </Paper>

      {/* Dialog for Edit */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Staff</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Staff Code"
                disabled
                value={form.staff_code}
                onChange={(e) => setForm({ ...form, staff_code: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Aadhar No"
                value={form.aadhar_no}
                onChange={(e) => setForm({ ...form, aadhar_no: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Salutation"
                value={form.salutation}
                onChange={(e) => setForm({ ...form, salutation: e.target.value })}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField
                label="Staff Name"
                value={form.staff_name}
                onChange={(e) => setForm({ ...form, staff_name: e.target.value })}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="DOB"
                type="date"
                value={form.dob}
                onChange={(e) => setForm({ ...form, dob: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="DOJ"
                type="date"
                value={form.doj}
                onChange={(e) => setForm({ ...form, doj: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="DOR"
                type="date"
                value={form.dor}
                onChange={(e) => setForm({ ...form, dor: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Gender"
                value={form.gender}
                onChange={(e) => setForm({ ...form, gender: e.target.value })}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Qualification"
                value={form.qualification}
                onChange={(e) => setForm({ ...form, qualification: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Designation"
                value={form.designation}
                onChange={(e) => setForm({ ...form, designation: e.target.value })}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Staff Type"
                value={form.staff_type}
                onChange={(e) => setForm({ ...form, staff_type: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="DOJ Time"
                value={form.doj_time}
                onChange={(e) => setForm({ ...form, doj_time: e.target.value })}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="DOR Time"
                value={form.dor_time}
                onChange={(e) => setForm({ ...form, dor_time: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Mobile"
                value={form.mobile}
                onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField
                label="Email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Staff Status"
                select
                fullWidth
                value={form.staff_status || "ACTIVE"}
                onChange={(e) => setForm({ ...form, staff_status: e.target.value })}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true }}
              >
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
                <option value="RELIEVED">RELIEVED</option>
                <option value="LONGLEAVE">LONGLEAVE</option>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              textTransform: "none",
              "&:hover": { backgroundColor: "#115293" },
            }}
            onClick={handleSubmit}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
